import { CarModelConfig } from "./ModelLoadController";
import {
  Scene,
  Vector3,
  Object3D,
  Box3,
  BoxBufferGeometry,
  MeshBasicMaterial,
  Mesh
} from "three";
import { AnimationController } from "../AnimationController";

export function doModelLoadIntroAnimation(
  config:CarModelConfig,
  scene:Scene,
  duration:number,
  animationController:AnimationController,
  onFinished = () => {}
):void {
  const TAU = Math.PI * 2;
  // const EPSILON = 0.000001;

  const getWheelRadius = (wheel:Object3D):number => {
    const box = new Box3().setFromObject(wheel);

    return (box.max.y - box.min.y) * 0.5;
  };

  // x = depth
  // y = vertical
  // z = horizontal
  const path = [
    new Vector3( 3.5, 0, 8.5),
    new Vector3( 0, 0, 5),
    new Vector3( 0, 0, 0),
    new Vector3( 0, 0, -10)
  ];
  /* Note: we subtract two instead of one because we don't actually
   * want to drive to the last knot of the path, it only exists
   * to control the final orientation of the car.
   */
  const pathSegmentCount = path.length - 2;

  const pathSegmentLengths = [];

  const samplePath = (t:number, useConstantVelocity:boolean):Vector3 => {
    t = Math.min(1, Math.max(0, t));

    let index;
    
    if (useConstantVelocity) {
      let runningLength = 0;

      for (index = 0; index < pathSegmentCount; index++) {
        let length = pathSegmentLengths[index];
        let updatedLength = runningLength + length;
        
        if (t <= updatedLength || index == pathSegmentCount-1) {
          t = (t - runningLength) / length;
          break;
        }
        
        runningLength = updatedLength;
      }
    } else {
      const scaled = t * pathSegmentCount;
      index = Math.floor(scaled);
      t = scaled - index;
    }

    const points = [
      path[Math.max(0, index - 1)],
      path[index],
      path[Math.min(path.length - 1, index + 1)],
      path[Math.min(path.length - 1, index + 2)]
    ];

    // // 3D hermite interpolation:
    // // http://paulbourke.net/miscellaneous/interpolation/
    // const tension = -1;
    // const bias = 0;
    // const tt = t * t;
    // const ttt = tt * t;
    // const tmpA = (1 + bias) * (1 - tension) * 0.5;
    // const tmpB = (1 - bias) * (1 - tension) * 0.5;
    // const m0 = new Vector3(
    //   (points[1].x - points[0].x) * tmpA * (points[2].x - points[1].x) * tmpB,
    //   (points[1].y - points[0].y) * tmpA * (points[2].y - points[1].y) * tmpB,
    //   (points[1].z - points[0].z) * tmpA * (points[2].z - points[1].z) * tmpB
    // );
    // const m1 = new Vector3(
    //   (points[2].x - points[1].x) * tmpA * (points[3].x - points[2].x) * tmpB,
    //   (points[2].y - points[1].y) * tmpA * (points[3].y - points[2].y) * tmpB,
    //   (points[2].z - points[1].z) * tmpA * (points[3].z - points[2].z) * tmpB
    // );
    // const a0 = 2 * ttt - 3 * tt + 1;
    // const a1 = ttt - 2 * tt + t;
    // const a2 = ttt - tt;
    // const a3 = -2 * ttt + 3 * tt; 
    // return new Vector3(
    //   a0 * points[1].x + a1 * m0.x + a2 * m1.x + a3 * points[2].x,
    //   a0 * points[1].y + a1 * m0.y + a2 * m1.y + a3 * points[2].y,
    //   a0 * points[1].z + a1 * m0.z + a2 * m1.z + a3 * points[2].z
    // );

    // 3D cubic interpolation with Catmull-Rom spline:
    // http://paulbourke.net/miscellaneous/interpolation/
    const a0 = new Vector3(
      -0.5 * points[0].x + 1.5 * points[1].x - 1.5 * points[2].x + 0.5 * points[3].x,
      -0.5 * points[0].y + 1.5 * points[1].y - 1.5 * points[2].y + 0.5 * points[3].y,
      -0.5 * points[0].z + 1.5 * points[1].z - 1.5 * points[2].z + 0.5 * points[3].z
    );
    const a1 = new Vector3(
      points[0].x - 2.5 * points[1].x + 2 * points[2].x - 0.5 * points[3].x,
      points[0].y - 2.5 * points[1].y + 2 * points[2].y - 0.5 * points[3].y,
      points[0].z - 2.5 * points[1].z + 2 * points[2].z - 0.5 * points[3].z
    );
    const a2 = new Vector3(
      -0.5 * points[0].x + 0.5 * points[2].x,
      -0.5 * points[0].y + 0.5 * points[2].y,
      -0.5 * points[0].z + 0.5 * points[2].z
    );
    const a3 = points[1];
    const tt = t * t;
    return new Vector3(
      a0.x * t * tt + a1.x * tt + a2.x * t + a3.x,
      a0.y * t * tt + a1.y * tt + a2.y * t + a3.y,
      a0.z * t * tt + a1.z * tt + a2.z * t + a3.z
    );
  };

  const nodes = ["INTERIOR", "INTERIOR_LINES", "EXTERIOR", "EXTERIOR_LINES", "WHEELS"].map(
    n => scene.getObjectByName(n));

  const wheels = config.driving.wheels.map(
    n => scene.getObjectByName(n));

  const timeBegin = performance.now();

  const wheel = wheels.filter(w => !!w)[0];
  const wheelRadius = getWheelRadius(wheel);

  const positionOld = new Vector3();
  const velocity = new Vector3();

  let angleNow = 0;
  let angleOld;
  let timeOld = timeBegin;

  const token = animationController.acquireToken();

  const remapTime = (t:number):number => {
    if (t >= 1) return 1;
    t = Math.pow(Math.sin(t * Math.PI * 0.5), 4);
    return t;
  };

  const onFrame = () => {
    const timeNow = performance.now();
    const timeDelta = (timeNow - timeOld) / duration;
    const t = remapTime((timeNow - timeBegin) / duration);
    const positionNow = samplePath(t, true);

    // TODO: use timeDelta for proper derivative?
    velocity.subVectors(positionNow, positionOld);

    // VW Mexico had an issue that the rotation seems to get stuck sometimes,
    // but we cannot reproduce it. I suspect maybe the EPSILON check is to blame (directly or indirectly),
    // so for now let's bypass it.
    // if (Math.abs(velocity.z) > EPSILON) {
    if (true) {
      angleNow = Math.atan2(velocity.x, velocity.z);

      // Normalize the angle into the 0 ... TAU range
      while (angleNow < 0  ) angleNow += TAU;
      while (angleNow > TAU) angleNow -= TAU;
    }
    if (angleOld === undefined) {
      angleOld = angleNow;
    }
    const angleDiff = (angleNow - angleOld) / timeDelta;

    for (const node of nodes) {
      if (node) {
        node.position.copy(positionNow);
        node.rotation.y = Math.PI + angleNow;
      }
    }

    const wheelDisplacement = velocity.length() / wheelRadius;
    for (const wheel of wheels) {
      if (wheel) {
        wheel.rotation.x -= wheelDisplacement;

        // Avoid floating point funkyness
        if (wheel.rotation.x <= -TAU) {
          wheel.rotation.x += TAU;
        }
      }
    }

    // Guard against extreme non-sense angles.
    // TODO: figure out why these occur in the first place.
    if (Math.abs(angleDiff) < Math.PI) {
      for (let i = 0; i < 2; i++) {
        const wheel = wheels[i];

        if (wheel) {
          wheel.rotation.y *= 0.8;
          wheel.rotation.y += 0.2 * (angleDiff * 0.05);
        }
      }
    }

    positionOld.copy(positionNow);
    angleOld = angleNow;
    timeOld = timeNow;

    if (t < 1) {
      requestAnimationFrame(onFrame);
    } else {
      animationController.releaseToken(token);
      onFinished();

      // See comment above about the rotation getting stuck for VW Mexico.
      // To be extra sure we explicitly force the final transform in the hope
      // to at least recover when things go wrong during the intro driving animation.
      for (const node of nodes) {
        if (node) {
          node.position.set(0, 0, 0);
          node.rotation.y = 0;
        }
      }
      for (const wheel of wheels) {
        if (wheel) {
          wheel.rotation.y = 0;
        }
      }
    }
  };

  const integratePathLength = (tA:number, tB:number):number => {
    let first = true;
    let length = 0.0;
    let posOld = new Vector3();

    // TODO: choose largest appropriate deltaT
    for (let t = tA; t <= tB; t += 0.00001) {
      const posNow = samplePath(t, false);
      if (!first) {
        // TODO: verify that the math is correct.
        const change = posNow.clone().sub(posOld);
        length += change.length();
      }
      first = false;
      posOld.copy(posNow);
    }

    return length;
  }

  // TODO: sum path length while iterating the pathSegmentCount?
  const totalLength = integratePathLength(0, 1);

  for (let i = 0; i < pathSegmentCount; i++) {
    const tA = i / pathSegmentCount;
    const tB = (i + 1) / pathSegmentCount;
    const length = integratePathLength(tA, tB);
    pathSegmentLengths.push(length / totalLength);
  }

  for (const wheel of wheels) {
    if (wheel) {
      wheel.rotation.order = "YXZ";
      wheel.rotation.x = totalLength / wheelRadius;
    }
  }

  // Path debug markers
  // for (const point of path) {
  //   const box = new Mesh(new BoxBufferGeometry(0.3, 0.3, 0.3), new MeshBasicMaterial({color: 0xFF0000}));
  //   box.position.copy(point);
  //   scene.add(box);
  // }
  // for (let t = 0; t <= 1.0; t += 0.1) {
  //   const box = new Mesh(new BoxBufferGeometry(0.25, 0.25, 0.25), new MeshBasicMaterial({color: 0x0000FF}));
  //   box.position.copy(samplePath(t, true));
  //   scene.add(box);
  // }

  onFrame();
}